import { useCallback, useEffect, useMemo, useState } from 'react'

import { DndContext, closestCenter } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { t } from 'i18next'

import { MIN_VISIBLE_CARDS } from '~/modules/retailMedia/constants/card'

import SortableItem from './SortableItem'

import {
  PanelContainer,
  PanelHeader,
  PanelList,
  ResetButton,
  VisibleCount
} from './styles'

const DrawerContent = <T extends string, TCard extends CardSelectorItem<T>>({
  cards,
  hiddenCards,
  defaultCardsToCustomize,
  defaultHiddenCards,
  onChange,
  onReset
}: CardSelectorDrawerProps<T, TCard>) => {
  const [orderedCards, setOrderedCards] = useState<TCard[]>(cards)
  const [draftHiddenCards, setDraftHiddenCards] = useState<T[]>(hiddenCards)

  useEffect(() => {
    setOrderedCards(cards)
    setDraftHiddenCards(hiddenCards)
  }, [cards, hiddenCards])

  const orderedTypes = useMemo(
    () => (orderedCards || []).map(card => card.type),
    [orderedCards]
  )

  const visibleCount = useMemo(
    () => orderedTypes.filter(type => !draftHiddenCards.includes(type)).length,
    [orderedTypes, draftHiddenCards]
  )

  const effectiveMinVisible = useMemo(
    () => Math.min(MIN_VISIBLE_CARDS, orderedTypes.length),
    [orderedTypes.length]
  )

  const saveDraft = useCallback(
    (nextOrderedCards: TCard[], nextHiddenCards: T[]) => {
      onChange({
        order: (nextOrderedCards || []).map(card => card.type),
        hidden: nextHiddenCards
      })
    },
    [onChange]
  )

  const handleDragEnd = useCallback(
    (event: { active: { id: string }; over: { id: string } | null }) => {
      const { active, over } = event

      if (!over || active.id === over.id) return

      const oldIndex = orderedCards.findIndex(c => c.type === active.id)
      const newIndex = orderedCards.findIndex(c => c.type === over.id)

      if (oldIndex < 0 || newIndex < 0) return

      const nextOrderedCards = arrayMove(orderedCards, oldIndex, newIndex)
      setOrderedCards(nextOrderedCards)
      saveDraft(nextOrderedCards, draftHiddenCards)
    },
    [draftHiddenCards, orderedCards, saveDraft]
  )

  const handleToggle = useCallback(
    (type: T) => {
      const isHidden = draftHiddenCards.includes(type)

      if (isHidden) {
        const nextHiddenCards = draftHiddenCards.filter(h => h !== type)
        setDraftHiddenCards(nextHiddenCards)
        saveDraft(orderedCards, nextHiddenCards)
        return
      }

      if (visibleCount <= effectiveMinVisible) return

      const nextHiddenCards = [...draftHiddenCards, type]
      setDraftHiddenCards(nextHiddenCards)
      saveDraft(orderedCards, nextHiddenCards)
    },
    [
      draftHiddenCards,
      effectiveMinVisible,
      orderedCards,
      saveDraft,
      visibleCount
    ]
  )

  const handleReset = useCallback(() => {
    setOrderedCards(defaultCardsToCustomize)
    setDraftHiddenCards(defaultHiddenCards)
    onReset()
  }, [defaultCardsToCustomize, defaultHiddenCards, onReset])

  return (
    <PanelContainer>
      <PanelHeader>
        <VisibleCount>
          {t('rm:metrics.cards.customize.visible', { count: visibleCount })}
        </VisibleCount>

        <ResetButton type="button" onClick={handleReset}>
          {t('rm:metrics.cards.customize.restoreDefault')}
        </ResetButton>
      </PanelHeader>

      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={orderedTypes}
          strategy={verticalListSortingStrategy}
        >
          <PanelList>
            {(orderedCards || []).map(card => {
              const isChecked = !draftHiddenCards.includes(card.type)

              return (
                <SortableItem
                  key={card.type}
                  card={card}
                  isChecked={isChecked}
                  isLocked={isChecked && visibleCount <= effectiveMinVisible}
                  onToggle={handleToggle}
                />
              )
            })}
          </PanelList>
        </SortableContext>
      </DndContext>
    </PanelContainer>
  )
}

export default DrawerContent
